import { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Stack, TextField, Button, Select, MenuItem, InputLabel, FormControl, Alert
} from '@mui/material';
import { Edit, Delete, Check, X } from 'lucide-react';
import { IAdvisor, IAdvisorActivityLedgerEntry, AdvisorActivityLedgerEntryStatus } from '../../../types/database';
import { useAdvisorShares } from '../hooks/useAdvisorShares';

export const SharesTab = ({
    advisor,
    onUpdate,
    onFieldUpdate
}: {
    advisor: IAdvisor,
    onUpdate: () => void,
    onFieldUpdate?: (updates: Partial<IAdvisor>) => void
}) => {
    const projectId = advisor.survey_link?.project?.id;
    const { activities, availableActivities, recordActivity, loadAdvisorLedger, reloadActivities, deleteActivityRecord, updateLedgerEntryStatus } = useAdvisorShares(projectId);

    const [records, setRecords] = useState<IAdvisorActivityLedgerEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activityId, setActivityId] = useState<number | ''>('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState(AdvisorActivityLedgerEntryStatus.APPROVED);
    const [addError, setAddError] = useState<string | null>(null);
    const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
    const [editingStatus, setEditingStatus] = useState<AdvisorActivityLedgerEntryStatus>(AdvisorActivityLedgerEntryStatus.APPROVED);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Auto-clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        fetchLedger();
    }, []);

    const fetchLedger = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error } = await loadAdvisorLedger(advisor.uuid);
            if (data) {
                setRecords(data.ledger);
            } else {
                setError(error?.message || 'Failed to load ledger');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load ledger');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        setAddError(null);
        setSuccessMessage(null);
        if (!activityId) {
            setAddError('Select an activity');
            return;
        }
        if (!projectId) {
            setAddError('Project ID is required');
            return;
        }

        try {
            const activity = activities.find(a => a.id === activityId);
            if (!activity) {
                setAddError('Invalid activity');
                return;
            }
            const payload = {
                key: activity.key,
                advisorId: advisor.uuid,
                projectId: projectId,
                notes,
                status: status
            };
            const { data, error } = await recordActivity(payload);
            if (data) {
                setActivityId('');
                setNotes('');
                setSuccessMessage('Activity added successfully');
                fetchLedger();
                reloadActivities();
            } else {
                setAddError(error?.message || 'Failed to add record');
            }
        } catch (err: any) {
            setAddError(err.message || 'Failed to add record');
        }
    };

    const handleDelete = async (recordId: number) => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const { success, error } = await deleteActivityRecord(recordId);
            if (success) {
                setSuccessMessage('Record deleted successfully');
                fetchLedger();
            } else {
                setError(error?.message || 'Failed to delete record');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete record');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (recordId: number) => {
        setUpdatingId(recordId);
        setUpdateError(null);
        setSuccessMessage(null);
        try {
            const { data, error } = await updateLedgerEntryStatus(recordId, editingStatus);
            if (data) {
                setEditingRecordId(null);
                setSuccessMessage('Status updated successfully');
                fetchLedger();
            } else {
                setUpdateError(error?.message || 'Failed to update status');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    const totalShares = records.reduce((sum, r) => sum + (r.shares_earned || 0), 0);

    if (loading && records.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>Loading ledger...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Activity</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Share Value</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records?.map(record => (
                        <TableRow key={record.id}>
                            <TableCell>{record.activity.name}</TableCell>
                            <TableCell>{new Date(record.approved_at ?? record.created_at).toLocaleString()}</TableCell>
                            <TableCell>{record.shares_earned}</TableCell>
                            <TableCell>
                                {editingRecordId === record.id ?
                                    <FormControl size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={editingStatus}
                                            size="small"
                                            label="Status"
                                            onChange={e => setEditingStatus(e.target.value as AdvisorActivityLedgerEntryStatus)}
                                        >
                                            <MenuItem value={AdvisorActivityLedgerEntryStatus.APPROVED}>{AdvisorActivityLedgerEntryStatus.APPROVED}</MenuItem>
                                            <MenuItem value={AdvisorActivityLedgerEntryStatus.PENDING}>{AdvisorActivityLedgerEntryStatus.PENDING}</MenuItem>
                                            <MenuItem value={AdvisorActivityLedgerEntryStatus.REJECTED}>{AdvisorActivityLedgerEntryStatus.REJECTED}</MenuItem>
                                            <MenuItem value={AdvisorActivityLedgerEntryStatus.ACQUIRED}>{AdvisorActivityLedgerEntryStatus.ACQUIRED}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    : record.status}
                            </TableCell>
                            <TableCell>
                                {editingRecordId === record.id ?
                                    <>
                                        <IconButton
                                            onClick={() => handleStatusUpdate(record.id)}
                                            disabled={updatingId === record.id}
                                            size="small"
                                        >
                                            <Check size={16} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => {
                                                setEditingRecordId(null);
                                                setUpdateError(null);
                                            }}
                                            disabled={updatingId === record.id}
                                            size="small"
                                        >
                                            <X size={16} />
                                        </IconButton>
                                    </>
                                    : <>
                                        <IconButton
                                            onClick={() => {
                                                setEditingRecordId(record.id);
                                                setEditingStatus(record.status);
                                            }}
                                            size="small"
                                        >
                                            <Edit size={16} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(record.id)}
                                            size="small"
                                        >
                                            <Delete size={16} />
                                        </IconButton>
                                    </>
                                }
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box mt={2} mb={3}>
                <Typography variant="subtitle2">Total Shares: {totalShares}</Typography>
            </Box>

            <Box mt={3}>
                <Typography variant="subtitle1">Add Activity to Ledger</Typography>
                {addError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{addError}</Alert>}
                <Stack direction="row" spacing={2} mt={1} mb={2} alignItems="center">
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Activity</InputLabel>
                        <Select
                            value={activityId}
                            onChange={e => setActivityId(Number(e.target.value))}
                            label="Activity"
                        >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {availableActivities.map(a => (
                                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            onChange={e => setStatus(e.target.value as AdvisorActivityLedgerEntryStatus)}
                            label="Status"
                        >
                            <MenuItem value={AdvisorActivityLedgerEntryStatus.APPROVED}>{AdvisorActivityLedgerEntryStatus.APPROVED}</MenuItem>
                            <MenuItem value={AdvisorActivityLedgerEntryStatus.PENDING}>{AdvisorActivityLedgerEntryStatus.PENDING}</MenuItem>
                            <MenuItem value={AdvisorActivityLedgerEntryStatus.REJECTED}>{AdvisorActivityLedgerEntryStatus.REJECTED}</MenuItem>
                            <MenuItem value={AdvisorActivityLedgerEntryStatus.ACQUIRED}>{AdvisorActivityLedgerEntryStatus.ACQUIRED}</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        sx={{ flexGrow: 1 }}
                    />
                </Stack>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                    {successMessage && <Typography variant="body1" color="success.main">{successMessage}</Typography>}
                    {updatingId && <Typography variant="body1" color="info.main">Updating...</Typography>}
                    {updateError && <Typography variant="body1" color="error.main">{updateError}</Typography>}
                    {addError && <Typography variant="body1" color="error.main">{addError}</Typography>}
                    <Button variant="contained" onClick={handleAdd}>Add</Button>
                </Box>
            </Box>
        </Box>
    );
}; 